import { JSX, ComponentChildren, RefObject } from 'preact';
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { normalizePosition } from '../utils/PRIVATE_floatingUtils';
import { Floating, ShiftOptions, RtlSide } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import { getAutofocusFocusables, focusOn, focusWithin } from '../utils/PRIVATE_tabbableUtils';
import { getClientHints } from '#utils/PRIVATE_clientHints';

// Hooks
import { useAnimation } from '../hooks/UNSAFE_useAnimation';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useBreakpointValues } from '../hooks/UNSAFE_useBreakpointValues';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { DrawerLayoutStyles } from './themes/DrawerLayoutStyles.css';
import { DrawerLayoutRedwoodTheme } from './themes/redwood/DrawerLayoutTheme';

// Types
type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Placement = 'start' | 'end' | 'bottom';
type Status = 'unmounted' | 'initial' | 'opening' | 'open' | 'closing';

type OnCloseDetail = {
  placement: Placement;
  reason: 'escapeKey';
};
type OnTransitionEndDetail = {
  placement: Placement;
  value: boolean;
};
type DisplayMode = 'overlay' | 'reflow';
type ResolvedDisplayMode = 'full-overlay' | 'overlay' | 'reflow';
type Visibility = 'hidden' | 'visible';

type Props = IntrinsicProps & {
  /**
   * The content node to be shown within the Start Drawer
   */
  startDrawer?: ComponentChildren;
  /**
   * The content node to be shown within the End Drawer
   */
  endDrawer?: ComponentChildren;
  /**
   * The content node to be shown within the Bottom Drawer
   */
  bottomDrawer?: ComponentChildren;
  /**
   * Specifies whether Start drawer is open.
   */
  isStartOpen?: boolean;
  /**
   * Specifies the display mode of the Start drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  startDisplay?: DisplayMode;
  /**
   * Specifies whether End drawer is open.
   */
  isEndOpen?: boolean;
  /**
   * Specifies the display mode of the End drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  endDisplay?: DisplayMode;
  /**
   * Specifies whether Bottom drawer is open.
   */
  isBottomOpen?: boolean;
  /**
   * Specifies the display mode of the Bottom drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  bottomDisplay?: DisplayMode;
  /**
   * Specifies callback triggered when a user tries to close a Drawer through UI interaction.
   * The parent should listen to this event and close the Drawer. If the parent fails to remove
   * the Popup, then no change will be done in the UI by the component.
   *
   * Supported detail values are:
   * <p><code>placement: start | end | bottom</code></p>
   * <p><code>reason: escapeKey</code></p>
   */
  onClose?: (detail: OnCloseDetail) => void;
  /**
   * Specifies callback triggered after the animation ends.
   *
   * Supported detail values are:
   * <p><code>placement: start | end | bottom</code></p>
   * <p><code>value: boolean</code></p>
   */
  onTransitionEnd?: (detail: OnTransitionEndDetail) => void;
} & TestIdProps;

/**
 * A drawer layout adds expandable side contents (drawers) alongside some primary content.
 *
 * These drawers automatically swap 'reflow' and 'overlay' display mode based on width of the page and can be placed at the 'start', 'end' or 'bottom' edge.
 */
export const DrawerLayout = ({
  children,
  startDrawer,
  endDrawer,
  bottomDrawer,
  isStartOpen = false,
  isEndOpen = false,
  isBottomOpen = false,
  startDisplay,
  endDisplay,
  bottomDisplay,
  onClose,
  onTransitionEnd,
  testId // ...props
}: Props) => {
  // Constants
  const zeroPx = '0px';
  const duration = 250;
  const verticalBreakpoint = 600;
  const stringStart = 'start';
  const stringEnd = 'end';
  const stringBottom = 'bottom';
  const stringInitial = 'initial';
  const stringOpening = 'opening';
  const stringOpen = 'open';
  const stringClosing = 'closing';
  const stringUnmounted = 'unmounted';
  const stringFullOverlay = 'full-overlay';
  const stringOverlay = 'overlay';
  const stringReflow = 'reflow';
  const stringHidden = 'hidden';
  const stringVisible = 'visible';

  // Initialize states
  const [startDrawerStatus, setStartDrawerStatus] = useState<Status>(
    isStartOpen ? stringInitial : stringUnmounted
  );
  const [endDrawerStatus, setEndDrawerStatus] = useState<Status>(
    isEndOpen ? stringInitial : stringUnmounted
  );
  const [bottomDrawerStatus, setBottomDrawerStatus] = useState<Status>(
    isBottomOpen ? stringInitial : stringUnmounted
  );
  const [startVisibility, setStartVisibility] = useState<Visibility>(stringHidden);
  const [endVisibility, setEndVisibility] = useState<Visibility>(stringHidden);
  const [bottomVisibility, setBottomVisibility] = useState<Visibility>(stringHidden);

  const [startOffset, setStartOffset] = useState<number>();
  const [endOffset, setEndOffset] = useState<number>();
  const [bottomOffset, setBottomOffset] = useState<number>();

  // Breakpoints:
  // sm: 0-600px (full-overlay)
  // md: 600-1024px (overlay)
  // lg: >1024px (reflow)
  const displayMode: ResolvedDisplayMode = useBreakpointValues({
    sm: stringFullOverlay,
    md: stringOverlay,
    lg: stringReflow
  });
  const [viewportDisplayModeHorizontal, setViewportDisplayModeHorizontal] =
    useState<ResolvedDisplayMode>(displayMode);
  const [viewportDisplayModeVertical, setViewportDisplayModeVertical] =
    useState<ResolvedDisplayMode>(displayMode);
  useEffect(() => {
    // Size breakpoint crossed
    setViewportDisplayModeHorizontal(displayMode);
  }, [displayMode]);

  // Refs
  const rootRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const startDrawerRef = useRef<HTMLDivElement>(null);
  const endDrawerRef = useRef<HTMLDivElement>(null);
  const bottomDrawerRef = useRef<HTMLDivElement>(null);

  const getElementHeight = (element: HTMLDivElement): number => {
    return element ? Math.round(element.offsetHeight) : 0;
  };

  const getElementHeightPx = useCallback((element: HTMLDivElement) => {
    return `${getElementHeight(element)}px`;
  }, []);

  const getElementWidth = useCallback((element: HTMLDivElement): number => {
    return element ? Math.round(element.getBoundingClientRect().width) : 0;
  }, []);

  const getElementWidthPx = useCallback(
    (element: HTMLDivElement): string => {
      return `${getElementWidth(element)}px`;
    },
    [getElementWidth]
  );

  // Normalizing position
  const { direction } = useUser();
  const isLtr = direction === 'ltr';

  const getNormalizedPlacement = useCallback(
    (placement: Placement): RtlSide => {
      return normalizePosition(placement, direction);
    },
    [direction]
  );

  const getTranslateX = (placement: Placement, ref: any): string => {
    return `${getNormalizedPlacement(placement) === 'right' ? '' : '-'}${getElementWidthPx(
      ref.current as HTMLDivElement
    )}`;
  };

  const getTranslateY = (ref: any): string => {
    return getElementHeightPx(ref.current as HTMLDivElement);
  };

  const isSidePlacement = (placement: Placement): boolean => {
    return [stringStart, stringEnd].indexOf(placement) > -1;
  };

  const isBottomPlacement = (placement: Placement): boolean => {
    return placement === stringBottom;
  };

  const isAnimationPending = (status: Status): boolean => {
    return [stringOpening, stringClosing].indexOf(status) > -1;
  };

  const isDrawerAnimationPending = (placement: Placement): boolean => {
    switch (placement) {
      case stringStart:
        return isAnimationPending(startDrawerStatus);
      case stringEnd:
        return isAnimationPending(endDrawerStatus);
      case stringBottom:
        return isAnimationPending(bottomDrawerStatus);
    }
  };

  const isDrawerOpen = (placement: Placement): boolean => {
    switch (placement) {
      case stringStart:
        return isStartOpen;
      case stringEnd:
        return isEndOpen;
      case stringBottom:
        return isBottomOpen;
    }
  };

  const getDrawerVisibility = (placement: Placement): Visibility => {
    switch (placement) {
      case stringStart:
        return startVisibility;
      case stringEnd:
        return endVisibility;
      case stringBottom:
        return bottomVisibility;
    }
  };

  const setDrawerVisibility = (placement: Placement, visibility: Visibility): void => {
    switch (placement) {
      case stringStart:
        return setStartVisibility(visibility);
      case stringEnd:
        return setEndVisibility(visibility);
      case stringBottom:
        return setBottomVisibility(visibility);
    }
  };

  const getDrawerRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawerRef;
      case stringEnd:
        return endDrawerRef;
      case stringBottom:
        return bottomDrawerRef;
    }
  };

  const getDrawerEl = (placement: Placement) => {
    return getDrawerRef(placement).current;
  };

  const getAnimatedNodeRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startAnimatedNodeRef;
      case stringEnd:
        return endAnimatedNodeRef;
      case stringBottom:
        return bottomAnimatedNodeRef;
    }
  };

  const getDrawerStatus = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawerStatus;
      case stringEnd:
        return endDrawerStatus;
      case stringBottom:
        return bottomDrawerStatus;
    }
  };

  const setDrawerStatus = (placement: Placement, status: Status): void => {
    switch (placement) {
      case stringStart:
        setStartDrawerStatus(status);
        break;
      case stringEnd:
        setEndDrawerStatus(status);
        break;
      case stringBottom:
        setBottomDrawerStatus(status);
        break;
    }
  };

  const resetAnimatedNodeRef = (placement: Placement): void => {
    switch (placement) {
      case stringStart:
        startAnimatedNodeRef(null);
        break;
      case stringEnd:
        endAnimatedNodeRef(null);
        break;
      case stringBottom:
        bottomAnimatedNodeRef(null);
        break;
    }
  };

  const getDrawerDisplayProp = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDisplay;
      case stringEnd:
        return endDisplay;
      case stringBottom:
        return bottomDisplay;
    }
  };

  const getDrawerDisplayMode = (placement: Placement): ResolvedDisplayMode => {
    const displayProp = getDrawerDisplayProp(placement);
    if (displayProp) {
      return displayProp;
    } else {
      if (isSidePlacement(placement)) {
        return viewportDisplayModeHorizontal;
      }
      return viewportDisplayModeVertical;
    }
  };

  // Animation config
  const getAnimationStates = (placement: Placement, ref: RefObject<HTMLElement>): any => {
    const bottomPlacement: boolean = isBottomPlacement(placement);
    if (getDrawerDisplayMode(placement) === stringReflow) {
      // Reflow display mode
      // Effect: Expand/Collapse
      if (bottomPlacement) {
        return {
          opening: () => {
            return {
              from: {
                maxHeight: zeroPx,
                overflowX: 'hidden'
              },
              to: {
                maxHeight: getElementHeightPx(ref.current as HTMLDivElement),
                overflowX: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                maxHeight: getElementHeightPx(ref.current as HTMLDivElement)
              },
              to: {
                maxHeight: zeroPx,
                overflowX: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          }
        };
      } else {
        return {
          opening: () => {
            return {
              from: {
                maxWidth: zeroPx,
                overflowY: 'hidden'
              },
              to: {
                maxWidth: getElementWidthPx(ref.current as HTMLDivElement),
                overflowY: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                maxWidth: getElementWidthPx(ref.current as HTMLDivElement)
              },
              to: {
                maxWidth: zeroPx,
                overflowY: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          }
        };
      }
    } else {
      // FullOverlay or Overlay display mode
      // Effect: Slidein/Slideout
      if (bottomPlacement) {
        return {
          opening: () => {
            return {
              from: {
                translateY: getTranslateY(ref)
              },
              to: {
                translateY: zeroPx
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                translateY: zeroPx
              },
              to: {
                translateY: getTranslateY(ref)
              },
              options: {
                duration: duration
              }
            };
          }
        };
      } else {
        return {
          opening: () => {
            return {
              from: {
                translateX: getTranslateX(placement, ref)
              },
              to: {
                translateX: zeroPx
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                translateX: zeroPx
              },
              to: {
                translateX: getTranslateX(placement, ref)
              },
              options: {
                duration: duration
              }
            };
          }
        };
      }
    }
  };

  const handleFocus = (placement: Placement) => {
    // Set focus to the first match:
    const drawerRef: RefObject<any> = getDrawerRef(placement);

    // 1. First element inside the drawer matching [autofocus]
    const autofocusItems: HTMLElement[] = getAutofocusFocusables(drawerRef.current);
    if (autofocusItems.length > 0) {
      focusOn(autofocusItems[0]);
      return;
    }
    // 2. Tabbable element inside the content element
    // 3. The drawer itself
    focusWithin(drawerRef.current);
  };

  // Custom hook to be shared for each side
  // It can't be a function as a hook can only be called within a custom hook
  function useNodeRef(placement: Placement, ref: RefObject<HTMLElement>) {
    const isOpen: boolean = isDrawerOpen(placement);
    const status = getDrawerStatus(placement);
    return useAnimation<Status, HTMLDivElement>(status, {
      animationStates: getAnimationStates(placement, ref),
      isAnimatedOnMount: true,
      onAnimationEnd: () => {
        if (status === stringOpening) {
          setDrawerStatus(placement, stringOpen);
          handleFocus(placement);
          onTransitionEnd?.({
            placement: placement,
            value: true
          });
        } else if (!isOpen) {
          resetAnimatedNodeRef(placement);
          setDrawerVisibility(placement, stringHidden);
          setDrawerStatus(placement, stringUnmounted);
          onTransitionEnd?.({
            placement: placement,
            value: false
          });
        }
      }
    }).nodeRef;
  }

  // Drawer refs
  const startAnimatedNodeRef = useNodeRef(stringStart, startDrawerRef);
  const endAnimatedNodeRef = useNodeRef(stringEnd, endDrawerRef);
  const bottomAnimatedNodeRef = useNodeRef(stringBottom, bottomDrawerRef);

  //testId support
  const testIdProps = useTestId(testId);

  // Custom hook to be shared for each side
  // It can't be a function as a hook can only be called within a custom hook
  function useDrawerStatus(placement: Placement): void {
    const isOpen = isDrawerOpen(placement);
    const drawerStatus = getDrawerStatus(placement);
    useEffect(() => {
      // Ignore initial render
      // 1. Ignore status update on initial render
      if (drawerStatus === stringUnmounted && !isOpen) {
        return;
      }

      // 2. Unmounted
      // Mount visually hidden component.
      // Component gets mounted, but will be visually hidden to avoid flickering.
      // Flickering happens because it gets rendered before animation hook repositions
      // it in next render cycle
      if (drawerStatus === stringUnmounted && isOpen) {
        setDrawerStatus(placement, stringInitial);
        return;
      }

      // 3. Initial
      // Setup animation
      // Component has been mounted.
      // We will kick out animation as node is already present in DOM.
      // To avoid flickering, we still keep it visually hidden until the next render cycle.
      if (drawerStatus === stringInitial && isOpen) {
        const drawerRef = getDrawerRef(placement);
        const animatedNodeRef = getAnimatedNodeRef(placement);
        animatedNodeRef(drawerRef.current);
        setDrawerStatus(placement, stringOpening);
        return;
      }

      // 4. Opening
      // Unhide component. It becomes visible.
      if (drawerStatus === stringOpening && isOpen) {
        setDrawerVisibility(placement, stringVisible);
        return;
      }

      // 5. Open
      // Status 'open' is set in 'onAnimationEnd' handler

      // 6. Closing
      if (!isOpen) {
        setDrawerStatus(placement, stringClosing);
      }
    }, [isOpen, drawerStatus, placement]);
  }

  // Drawers' status handler±
  useDrawerStatus(stringStart);
  useDrawerStatus(stringEnd);
  useDrawerStatus(stringBottom);

  // Keydown handlers
  const handleKeyDownStart = (event: Event): void => {
    handleKeyDown(stringStart, event);
  };
  const handleKeyDownEnd = (event: Event): void => {
    handleKeyDown(stringEnd, event);
  };
  const handleKeyDownBottom = (event: Event): void => {
    handleKeyDown(stringBottom, event);
  };

  const handleKeyDown = (placement: Placement, event: any): void => {
    if (event.code === 'Escape') {
      // Handle only in overlay mode
      if (isOverlay(placement)) {
        onClose?.({
          placement,
          reason: 'escapeKey'
        });
      }
    }
  };

  // Styles
  const { classes, styles } = useComponentTheme<never, DrawerLayoutStyles>(
    DrawerLayoutRedwoodTheme
  );

  const getVisibilityClass = (visibility: Visibility) => {
    return visibility === stringHidden ? styles.visibilityHidden : styles.visibilityVisible;
  };

  const isOverlay = (placement: Placement) => {
    return [stringFullOverlay, stringOverlay].indexOf(getDrawerDisplayMode(placement)) > -1;
  };

  const isFullOverlay = (placement: Placement): boolean => {
    return getDrawerDisplayMode(placement) === stringFullOverlay;
  };

  const getDrawerClassNames = (placement: Placement): string => {
    // Visibility style is always added
    const visibility: Visibility = getDrawerVisibility(placement);
    const classNamesArr: string[] = [getVisibilityClass(visibility)];

    // Styles for overlay vs. full-overlay vs. reflow
    if (isOverlay(placement)) {
      classNamesArr.push(styles.overlayDrawer);
      if (isSidePlacement(placement) && !isFullOverlay(placement)) {
        classNamesArr.push(styles.overlaySideDrawerNonFullWidth);
      }
    } else {
      classNamesArr.push(styles.reflowDrawer);
    }

    // Styles for animation
    if (isDrawerAnimationPending(placement)) {
      classNamesArr.push(styles.animatedDrawer);
    }

    const getPlacementSpecificStyles = (placement: Placement): string => {
      switch (placement) {
        case stringStart:
          if (isOverlay(stringStart)) {
            return isLtr ? styles.overlayDrawerLeft : styles.overlayDrawerRight;
          } else {
            return isLtr ? styles.reflowDrawerLeft : styles.reflowDrawerRight;
          }
        case stringEnd:
          if (isOverlay(stringEnd)) {
            return isLtr ? styles.overlayDrawerRight : styles.overlayDrawerLeft;
          } else {
            return isLtr ? styles.reflowDrawerRight : styles.reflowDrawerLeft;
          }
        case stringBottom:
          return isOverlay(stringBottom) ? styles.overlayDrawerBottom : styles.reflowDrawerBottom;
      }
    };
    // Placement specific styles
    classNamesArr.push(getPlacementSpecificStyles(placement));

    return classNames(classNamesArr);
  };

  const getDrawerProp = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawer;
      case stringEnd:
        return endDrawer;
      case stringBottom:
        return bottomDrawer;
    }
  };

  const getKeyDownHandler = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return handleKeyDownStart;
      case stringEnd:
        return handleKeyDownEnd;
      case stringBottom:
        return handleKeyDownBottom;
    }
  };

  // Single drawer template
  const renderDrawer = (placement: Placement) => {
    return (
      <div
        ref={getDrawerRef(placement)}
        className={getDrawerClassNames(placement)}
        tabIndex={isOverlay(placement) ? -1 : undefined}
        onKeyDown={getKeyDownHandler(placement)}>
        {getDrawerProp(placement)}
      </div>
    );
  };

  const clientHints = getClientHints();

  const getViewportHeight = useCallback((): number => {
    if (clientHints.platform === 'ios') {
      // On ios window.innerHeight is not recommended way of measuring the viewport
      return document.documentElement.clientHeight;
    }
    return window.innerHeight;
  }, [clientHints.platform]);

  const handleVerticalResize = useCallback(() => {
    const viewportHeight = getViewportHeight();
    if (viewportHeight >= verticalBreakpoint) {
      if (viewportDisplayModeVertical != stringReflow) {
        setViewportDisplayModeVertical(stringReflow);
      }
    } else {
      if (viewportDisplayModeVertical != stringOverlay) {
        setViewportDisplayModeVertical(stringOverlay);
      }
    }
  }, [viewportDisplayModeVertical, getViewportHeight]);

  // Window resize listener
  // useBreakpoint hook is designed for horizontal breakpoints only
  // This listener is used for 600px (overlay vs. reflow) breakpoint
  // used for bottom drawer
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      handleVerticalResize();
    });
    resizeObserver.observe(document.body);
    // Deregister resize observer when Drawer layout unmounts
    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, [handleVerticalResize]);

  const getOffset = (placement: Placement): number => {
    const drawerEl = getDrawerEl(placement);
    if (drawerEl) {
      return isSidePlacement(placement) ? -getElementWidth(drawerEl) : -getElementHeight(drawerEl);
    }
    return 0;
  };

  const getOffsetState = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startOffset;
      case stringEnd:
        return endOffset;
      case stringBottom:
        return bottomOffset;
    }
  };

  const setSideOverlayDrawerFullHeight = useCallback(
    (placement: Placement) => {
      // By default PopupElements have no predefined height.
      // In 'overlay' mode drawers should be
      // of the same height as their parent <oj-drawer-layout>
      const centerSectionHeight = getElementHeightPx(centerRef.current as HTMLDivElement);

      const drawerEl = getDrawerRef(placement).current;
      if (drawerEl && isDrawerOpen(placement) && isOverlay(placement)) {
        drawerEl.style.height = centerSectionHeight;
      }
    },
    [getElementHeightPx, isDrawerOpen, isOverlay]
  );

  const handleSideDrawerFullWidth = (placement: Placement) => {
    const drawerEl = getDrawerRef(placement).current;
    if (drawerEl) {
      if (isDrawerOpen(placement)) {
        const drawerInlineStyle = drawerEl.style;
        if (isFullOverlay(placement)) {
          drawerInlineStyle.width = getElementWidthPx(rootRef.current as HTMLDivElement);
        } else {
          drawerInlineStyle.removeProperty('width');
        }
      }
    }
  };

  const setBottomOverlayDrawerWidth = useCallback(() => {
    const bottomDrawerElement = bottomDrawerRef.current;
    if (bottomDrawerElement && isDrawerOpen(stringBottom) && isOverlay(stringBottom)) {
      const centerSectionWidth = getElementWidthPx(centerRef.current as HTMLDivElement);
      bottomDrawerElement.style.width = centerSectionWidth;
    }
  }, [getElementWidthPx, isDrawerOpen, isOverlay]);

  const setDrawerSize = (placement: Placement) => {
    if (isSidePlacement(placement)) {
      setSideOverlayDrawerFullHeight(placement);
      handleSideDrawerFullWidth(placement);
    }
    if (isBottomPlacement(placement)) {
      setBottomOverlayDrawerWidth();
    }
  };

  const handleDrawerLayoutResize = useCallback(() => {
    // If the DrawerLayout (page content) changes size,
    // adjust OVERLAY side drawers (start/end) height to fit the drawer
    // Note that these two functions checks that 'overlay' condition is fulfilled
    setSideOverlayDrawerFullHeight(stringStart);
    setSideOverlayDrawerFullHeight(stringEnd);
    setBottomOverlayDrawerWidth();
  }, [setSideOverlayDrawerFullHeight, setBottomOverlayDrawerWidth]);
  // DrawerLayout resize observer
  useResizeObserver(rootRef, handleDrawerLayoutResize);

  const setOffsetState = (placement: Placement) => {
    const offset = getOffset(placement);
    switch (placement) {
      case stringStart:
        setStartOffset(offset);
        break;
      case stringEnd:
        setEndOffset(offset);
        break;
      case stringBottom:
        setBottomOffset(offset);
        break;
    }
  };

  // Start drawer resize observer
  const handleStartDrawerResize = () => {
    setOffsetState(stringStart);
  };
  useResizeObserver(startDrawerRef, handleStartDrawerResize);

  // End drawer resize observer
  const handleEndDrawerResize = () => {
    setOffsetState(stringEnd);
  };
  useResizeObserver(endDrawerRef, handleEndDrawerResize);

  // Bottom drawer resize observer
  const handleBottomDrawerResize = () => {
    setOffsetState(stringBottom);
  };
  useResizeObserver(bottomDrawerRef, handleBottomDrawerResize);

  const getReflowOrOverlayDrawer = (placement: Placement) => {
    const shiftOptions: ShiftOptions = {
      mainAxis: false,
      crossAxis: false
    };

    const flipOptions = {
      mainAxis: false,
      crossAxis: false
    };

    // Reflow vs. overlay drawer
    if (isOverlay(placement)) {
      setDrawerSize(placement);

      // Initial offset
      if (!getOffsetState(placement)) {
        setOffsetState(placement);
      }

      return (
        // Reconciliation issue. Reparented node must
        // be a single child of a <div>
        <div>
          <Layer>
            <Floating
              anchorRef={placement === stringBottom ? centerRef : rootRef}
              placement={placement}
              class={isDrawerAnimationPending(placement) ? styles.floating : ''}
              offsetValue={getOffsetState(placement)}
              flipOptions={flipOptions}
              shiftOptions={shiftOptions}>
              {renderDrawer(placement)}
            </Floating>
          </Layer>
        </div>
      );
    } else {
      return renderDrawer(placement);
    }
  };

  // Render drawer conditionally
  const getDrawer = (placement: Placement): any => {
    const isMounted = getDrawerStatus(placement) !== stringUnmounted;
    return isMounted ? getReflowOrOverlayDrawer(placement) : null;
  };

  return (
    <div ref={rootRef} className={classes} {...testIdProps}>
      {/*Start drawer*/}
      {getDrawer(stringStart)}
      {/*Center section*/}
      <div ref={centerRef} className={styles.centerSection}>
        <div className={styles.mainContent}>
          {/*<main content*/}
          {children}
        </div>
        {/*Bottom drawer*/}
        {getDrawer(stringBottom)}
      </div>
      {/*End drawer*/}
      {getDrawer(stringEnd)}
    </div>
  );
};
